import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Trophy, Star, Users, Sparkles, LogOut, Flame, Award, 
  ChevronRight, Calendar, HelpCircle, FolderOpen, Target, 
  Briefcase, CheckCircle, Brain, MapPin, User, ArrowLeft
} from 'lucide-react';

// Summer Arcade Theme - Bold, high-energy, social-media inspired
// Colors: Indigo primary (#4F46E5), Neon Green (#00E054), Yellow accent (#FFD60A)
// Font: Bricolage Grotesque (headings), Manrope (body)
// Style: Brutalist shadows, rounded corners, bold borders

const SummerArcadePreview = () => {
  const navigate = useNavigate();
  
  // Mock user data for preview
  const user = {
    name: 'Brandon Williams',
    total_points: 1250,
    current_streak: 7,
    program_type: 'upnext',
    work_site: 'Newark Public Library',
    profile_picture: null
  };

  return (
    <div className="min-h-screen pb-24" style={{ 
      backgroundColor: '#FAFAFA',
      fontFamily: "'Manrope', sans-serif"
    }}>
      {/* Back Button */}
      <button 
        onClick={() => navigate(-1)}
        className="fixed top-4 left-4 z-50 bg-white border-2 border-black rounded-full p-2 shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:shadow-[1px_1px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] transition-all"
      >
        <ArrowLeft className="w-5 h-5" />
      </button>

      {/* Theme Label */}
      <div className="fixed top-4 right-4 z-50 bg-[#4F46E5] text-white px-4 py-2 rounded-full border-2 border-black shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] font-bold text-sm">
        Theme 1: Summer Arcade
      </div>

      {/* Hero Header */}
      <div className="relative overflow-hidden" style={{ backgroundColor: '#4F46E5' }}>
        {/* Decorative circles */}
        <div className="absolute top-[-50px] right-[-50px] w-40 h-40 rounded-full" style={{ backgroundColor: '#00E054', opacity: 0.3 }} />
        <div className="absolute bottom-[-30px] left-[-30px] w-32 h-32 rounded-full" style={{ backgroundColor: '#FFD60A', opacity: 0.3 }} />
        
        <div className="relative px-4 pt-16 pb-8">
          <div className="max-w-md mx-auto">
            {/* Logo Bar */}
            <div className="flex justify-between items-center mb-6">
              <div className="bg-white rounded-2xl p-2 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                <div className="w-10 h-10 bg-[#4F46E5] rounded-xl flex items-center justify-center">
                  <span className="text-white font-bold text-lg">N</span>
                </div>
              </div>
              <div className="flex gap-2">
                <button className="bg-white/20 backdrop-blur rounded-full p-3 border-2 border-white/30">
                  <LogOut className="w-5 h-5 text-white" />
                </button>
              </div>
            </div>

            {/* Welcome Section */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-20 h-20 rounded-2xl border-4 border-white shadow-[4px_4px_0px_0px_rgba(0,0,0,0.3)] bg-[#00E054] flex items-center justify-center">
                <span className="text-3xl font-bold text-white" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>B</span>
              </div>
              <div className="text-white">
                <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                  Hey, Brandon!
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <span className="bg-[#00E054] text-black text-xs font-bold px-3 py-1 rounded-full border border-black">
                    UpNEXT SYEP
                  </span>
                </div>
              </div>
            </div>

            {/* Worksite Info */}
            <div className="bg-white/15 backdrop-blur rounded-2xl p-4 mb-6 border-2 border-white/30">
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-[#FFD60A]" />
                <div>
                  <p className="text-white/70 text-xs uppercase font-bold tracking-wider">Worksite</p>
                  <p className="text-white font-semibold">Newark Public Library</p>
                </div>
              </div>
            </div>

            {/* Stats Grid - Bento Style */}
            <div className="grid grid-cols-2 gap-4">
              {/* Points Card */}
              <div className="bg-white rounded-2xl p-5 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:translate-y-[-2px] transition-all">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FFD60A' }}>
                    <Star className="w-4 h-4 text-black" fill="currentColor" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-600">Points</span>
                </div>
                <p className="text-4xl font-extrabold text-[#111827]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                  1,250
                </p>
              </div>

              {/* Streak Card */}
              <div className="bg-white rounded-2xl p-5 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:translate-y-[-2px] transition-all">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center">
                    <Flame className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-xs font-bold uppercase tracking-wider text-gray-600">Streak</span>
                </div>
                <p className="text-4xl font-extrabold text-[#111827]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                  7<span className="text-lg text-gray-400 ml-1">days</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        
        {/* Quick Actions - Horizontal Scroll */}
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4">
          {[
            { icon: Target, label: 'Goals', color: '#00E054' },
            { icon: Briefcase, label: 'Project', color: '#FF6B35' },
            { icon: Trophy, label: 'Rankings', color: '#FFD60A' },
            { icon: Award, label: 'Wins', color: '#FF4D6D' },
            { icon: FolderOpen, label: 'Resources', color: '#F59E0B' },
            { icon: HelpCircle, label: 'Help', color: '#4F46E5' },
          ].map((item) => (
            <button
              key={item.label}
              className="flex-shrink-0 bg-white rounded-2xl p-4 min-w-[90px] border-2 border-black shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] hover:shadow-[5px_5px_0px_0px_rgba(0,0,0,1)] hover:translate-y-[-2px] transition-all"
            >
              <div className="w-10 h-10 rounded-xl mx-auto mb-2 flex items-center justify-center" style={{ backgroundColor: item.color }}>
                <item.icon className="w-5 h-5 text-white" />
              </div>
              <p className="text-xs font-bold text-center text-[#111827]">{item.label}</p>
            </button>
          ))}
        </div>

        {/* Work Journal Card - Primary CTA */}
        <div className="bg-[#00E054] rounded-3xl p-6 border-2 border-black shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] relative overflow-hidden">
          <div className="absolute top-[-20px] right-[-20px] w-24 h-24 rounded-full bg-white/20" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-white rounded-xl border-2 border-black flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-[#00E054]" />
              </div>
              <div>
                <h2 className="text-xl font-extrabold text-black" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
                  Work Journal
                </h2>
                <p className="text-black/70 text-sm font-medium">Share your weekly wins</p>
              </div>
            </div>
            <button className="w-full bg-black text-white rounded-full py-4 font-bold text-lg hover:bg-gray-800 transition-colors border-2 border-black shadow-[3px_3px_0px_0px_rgba(255,255,255,0.3)]">
              Drop a Win! +50 pts
            </button>
          </div>
        </div>

        {/* This Week Section */}
        <div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#4F46E5]/10 border-2 border-[#4F46E5]/30 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-[#4F46E5]" />
            </div>
            <h2 className="text-xl font-extrabold text-[#111827]" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
              This Week
            </h2>
          </div>

          <div className="space-y-3">
            {/* Assessment Card */}
            <div className="bg-gradient-to-r from-violet-600 to-indigo-600 rounded-2xl p-5 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] cursor-pointer hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:translate-y-[-2px] transition-all">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-white">
                    <h3 className="font-bold text-lg">Skills Assessment</h3>
                    <p className="text-white/80 text-sm">Track your growth • 50 pts</p>
                  </div>
                </div>
                <span className="bg-[#FFD60A] text-black text-xs font-bold px-3 py-1 rounded-full border border-black">NEW</span>
              </div>
            </div>

            {/* Check-in Card */}
            <div className="bg-white rounded-2xl p-4 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:translate-y-[-2px] transition-all cursor-pointer">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#4F46E5]/10 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-[#4F46E5]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#111827]">Weekly Check-in</h3>
                    <p className="text-gray-500 text-sm">Share your experience • 20 pts</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </div>

            {/* Trivia Card */}
            <div className="bg-white rounded-2xl p-4 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:translate-y-[-2px] transition-all cursor-pointer">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#00E054]/10 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-[#00E054]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#111827]">Weekly Trivia</h3>
                    <p className="text-gray-500 text-sm">Test your knowledge • 25 pts</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Team Card */}
        <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-violet-700 rounded-3xl p-5 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] cursor-pointer hover:shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] hover:translate-y-[-2px] transition-all">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-white" />
              </div>
              <div className="text-white">
                <h3 className="font-bold text-lg" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>Team Alpha</h3>
                <p className="text-white/70 text-sm">8 members</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-extrabold text-white" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>4,500</p>
              <p className="text-white/70 text-xs">team pts</p>
            </div>
          </div>
          <button className="w-full bg-white/20 hover:bg-white/30 rounded-full py-3 text-white font-bold border-2 border-white/30 transition-colors">
            View Team <ChevronRight className="w-4 h-4 inline ml-1" />
          </button>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-black px-4 py-3">
        <div className="max-w-md mx-auto flex justify-around items-center">
          {[
            { icon: '🏠', label: 'Home', active: true },
            { icon: '🎯', label: 'Goals', active: false },
            { icon: '📊', label: 'Progress', active: false },
            { icon: '👤', label: 'Profile', active: false },
          ].map((item) => (
            <button 
              key={item.label} 
              className={`flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all ${
                item.active 
                  ? 'bg-[#4F46E5] text-white' 
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              <span className="text-xl">{item.icon}</span>
              <span className="text-xs font-bold">{item.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SummerArcadePreview;
